import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

const schema = z.object({
  password: z.string().min(8, 'رمز عبور باید حداقل ۸ کاراکتر باشد'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: 'رمزهای عبور یکسان نیستند',
  path: ['confirm'],
})

type FormData = z.infer<typeof schema>

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // session restored from link — ready to update
      }
    })
  }, [])

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  })

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setError(null)
    const { error: err } = await supabase.auth.updateUser({ password: data.password })
    setIsSubmitting(false)
    if (err) {
      setError(err.message)
    } else {
      setSuccess(true)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-full max-w-[420px] mx-4">
        <div className="bg-card border border-border rounded-xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mb-4">
              <span className="text-primary-foreground font-bold text-xl font-mono">M</span>
            </div>
            <h1 className="text-xl font-bold text-foreground">تنظیم رمز عبور جدید</h1>
          </div>

          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-secondary mx-auto" />
              <p className="text-sm text-foreground">رمز عبور با موفقیت تغییر یافت.</p>
              <Button className="w-full" onClick={() => navigate('/sign-in')}>
                ورود به سیستم
              </Button>
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-center gap-2 bg-destructive/15 border border-destructive/30 rounded-lg px-4 py-3 mb-4 text-sm text-red-400">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>رمز عبور جدید</FormLabel>
                        <FormControl>
                          <Input type="password" dir="ltr" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirm"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>تکرار رمز عبور</FormLabel>
                        <FormControl>
                          <Input type="password" dir="ltr" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'در حال ذخیره...' : 'ذخیره رمز عبور'}
                  </Button>
                </form>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
